'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { format, parse } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAnniversaries } from '@/hooks/use-anniversaries';
import type { Anniversary, AnniversaryInput } from '@/lib/types';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  date: z.string().refine((val) => /^\d{2}\/\d{2}\/\d{4}$/.test(val), {
    message: 'Date must be in DD/MM/YYYY format.',
  }),
  time: z.string().optional(),
  type: z.string().min(2, 'Type is required.'),
  notes: z.string().optional(),
});

type AnniversaryFormProps = {
  anniversary?: Anniversary;
  onSuccess: () => void;
};

export function AnniversaryForm({ anniversary, onSuccess }: AnniversaryFormProps) {
  const { addAnniversary, updateAnniversary } = useAnniversaries();
  
  const defaultDate = anniversary?.date ? format(parse(anniversary.date, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy') : '';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: anniversary
      ? { ...anniversary, date: defaultDate }
      : {
          name: '',
          date: '',
          time: '',
          type: 'Wedding',
          notes: '',
        },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    const formattedValues: AnniversaryInput = {
      ...values,
      date: format(parse(values.date, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'),
    };

    if (anniversary) {
      updateAnniversary({ ...anniversary, ...formattedValues });
    } else {
      addAnniversary(formattedValues);
    }

    onSuccess();
  }

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueLength = rawValue.length;
    let formattedValue = rawValue;

    if (valueLength > 2) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2)}`;
    }
    if (valueLength > 4) {
      formattedValue = `${rawValue.slice(0, 2)}/${rawValue.slice(2, 4)}/${rawValue.slice(4, 8)}`;
    }
    
    field.onChange(formattedValue.slice(0, 10));
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. John & Jane's Wedding" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Date</FormLabel>
                <FormControl>
                  <Input 
                    type="text" 
                    placeholder="DD/MM/YYYY" 
                    {...field}
                    onChange={(e) => handleDateChange(e, field)}
                    maxLength={10}
                  />
                </FormControl>
                <FormDescription>
                  Date as DD/MM/YYYY.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Time (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="time"
                    {...field}
                  />
                </FormControl>
                 <FormDescription>
                  Time of the event.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="Wedding">Wedding</SelectItem>
                  <SelectItem value="Work">Work</SelectItem>
                  <SelectItem value="Relationship">Relationship</SelectItem>
                  <SelectItem value="First Date">First Date</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Details about the anniversary..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Add any notes that might be helpful.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit">{anniversary ? 'Save Changes' : 'Add Anniversary'}</Button>
      </form>
    </Form>
  );
}
